import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SBINModal from "../components/modals/SBINModal";
import ConfirmModal from "../components/modals/ConfirmModal";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import SuperAdminMarketplaceSection from "../components/superadmin/SuperAdminMarketplaceSection";
import { useAuth } from "../context/AuthContext";
import {
  createSalon,
  deleteEmptyBusiness,
  deleteEmptySalon,
  fetchAllBusinesses,
  fetchAllSalons,
  fetchSalonById,
  fetchSalonUsers,
  fetchAllUsers,
  resetUserPassword,
  switchBusiness,
  switchSalon,
} from "../api/superadmin";

const TOOLTIP_TEXT = {
  businesses:
    "Businesses group one or more salons. Set a business as current to filter salons and users.",
  setCurrentBusiness:
    "Sets this business as your current working context for filtering salons and users.",
  salons:
    "Salons are the locations that take bookings. Select a salon to manage it or switch into its context.",
  switchSalon:
    "Switches your session into this salon so you can use the system as if you logged into that salon.",
  users:
    "Shows all users across salons. Use this to troubleshoot logins and reset passwords.",
  resetPassword:
    "Sets a temporary password for the user. You can force them to change it on next login.",
  search:
    "Search by salon name, business code, username, or email.",
  quickAccess:
    "Type a Salon ID to quickly find and switch into that salon.",
  businessFilter:
    "Filter salons by business context.",
  salonFilter:
    "Filter salons to one location.",
  userBusinessFilter:
    "Filter users by business.",
  userSalonFilter:
    "Filter users by salon.",
};

const HELP_SECTIONS = [
  {
    id: "overview",
    title: "What this console does",
    description: "Manage business context, salons, and users from one Super Admin page.",
    anchorId: "sa-overview",
    bullets: [
      "Switch context to a business or salon.",
      "Create salons and delete empty businesses/salons.",
      "Review users and reset passwords when needed.",
    ],
    tips: [
      "Confirm the selected business before making changes.",
      "Use filters to narrow long lists quickly.",
    ],
  },
  {
    id: "salons",
    title: "Salons",
    description: "Filter salons, switch to salon context, and create salon setup shells.",
    anchorId: "sa-salons",
    bullets: [
      "Use Quick Salon ID to jump directly to one salon.",
      "Use Business, Salon, and Search filters.",
      "Switch to Salon opens that salon context.",
      "Create Salon appears when a business has no salons.",
    ],
    tips: ["Use search plus business filter to reduce long lists."],
  },
  {
    id: "businesses",
    title: "Businesses",
    description: "Set current business context and manage empty businesses.",
    anchorId: "sa-businesses",
    bullets: [
      "Set Current updates business context for filtering.",
      "Delete Empty is only available when no linked data remains.",
    ],
    tips: ["Do not delete a business unless blockers show zero."],
  },
  {
    id: "users",
    title: "Users",
    description: "Search users and reset passwords across all businesses and salons.",
    anchorId: "sa-users",
    bullets: [
      "Filter users by business and salon.",
      "Use Reset Password for account recovery.",
      "Optionally force password change on next login.",
    ],
    tips: ["Confirm the correct user before resetting credentials."],
  },
  {
    id: "marketplace",
    title: "Marketplace",
    description:
      "Review booth inventory, marketplace readiness, owners, and salons that still have no listings.",
    anchorId: "sa-marketplace",
    bullets: [
      "Use the shared business context to narrow marketplace scope.",
      "Inspect booth detail before making availability changes.",
      "Availability changes require a reason and are audited.",
      "Readiness rows drill back into the booth inventory.",
    ],
    tips: [
      "Use Not Ready only to work the biggest setup gaps first.",
      "Filter salons without booths back into the salon section for follow-up.",
    ],
  },
];

const TROUBLESHOOTING_ITEMS = [
  {
    title: "No salons found",
    body: "Check selected business and salon filters, then clear search.",
  },
  {
    title: "Cannot delete business or salon",
    body: "Deletion is allowed only when blockers are empty.",
  },
  {
    title: "Users not loading",
    body: "Retry with fewer filters and verify the selected business/salon.",
  },
  {
    title: "Marketplace rows look incomplete",
    body: "Open booth detail to confirm whether the booth is missing photos, pricing, hours, or description.",
  },
];

const SUPERADMIN_SELECTED_BUSINESS_KEY = "sbin_superadmin_selected_business_id";
const USERS_PAGE_SIZE = 25;

const readStoredSelectedBusinessId = () => {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(SUPERADMIN_SELECTED_BUSINESS_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const persistSelectedBusinessId = (businessId) => {
  if (typeof window === "undefined") return;
  if (businessId == null || businessId === "") {
    window.localStorage.removeItem(SUPERADMIN_SELECTED_BUSINESS_KEY);
    return;
  }
  const numeric = Number(businessId);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    window.localStorage.removeItem(SUPERADMIN_SELECTED_BUSINESS_KEY);
    return;
  }
  window.localStorage.setItem(SUPERADMIN_SELECTED_BUSINESS_KEY, String(numeric));
};

const resolveBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch (err) {
    return "America/New_York";
  }
};

const BLOCKER_LABELS = {
  salons: "salons",
  clients: "clients",
  staff: "staff",
  services: "services",
  appointments: "appointments",
  tickets: "tickets",
  time_clock_entries: "time logs",
  booths: "booths",
  portal_registrations: "portal registrations",
  cash_drawers: "cash drawers",
  daily_closing_reports: "daily closings",
};

const formatBlockers = (blockers, { nonZeroOnly = true } = {}) => {
  if (!blockers || typeof blockers !== "object") return "";
  return Object.entries(blockers)
    .filter(([, value]) => {
      const count = Number(value || 0);
      return nonZeroOnly ? count > 0 : true;
    })
    .map(([key, value]) => `${BLOCKER_LABELS[key] || key}=${Number(value || 0)}`)
    .join(", ");
};

export default function SuperAdminConsole() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, is_super_admin } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [salons, setSalons] = useState([]);
  const [allSalons, setAllSalons] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [selectedBusinessId, setSelectedBusinessId] = useState(() => {
    const storedBusinessId = readStoredSelectedBusinessId();
    return user?.business_id ?? storedBusinessId ?? null;
  });
  const [selectedUserBusinessId, setSelectedUserBusinessId] = useState(() => {
    const storedBusinessId = readStoredSelectedBusinessId();
    const value = user?.business_id ?? storedBusinessId ?? null;
    return value ?? "";
  });
  const [selectedSalonFilterId, setSelectedSalonFilterId] = useState("");
  const [selectedUserSalonId, setSelectedUserSalonId] = useState("");
  const [salonSearch, setSalonSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [quickSalonIdInput, setQuickSalonIdInput] = useState("");
  const [quickSalon, setQuickSalon] = useState(null);
  const [quickSalonLoading, setQuickSalonLoading] = useState(false);
  const [quickSalonError, setQuickSalonError] = useState("");
  const [highlightedSalonId, setHighlightedSalonId] = useState(null);
  const [highlightedSectionId, setHighlightedSectionId] = useState("");
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const [loadingSalons, setLoadingSalons] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [switchingBusinessId, setSwitchingBusinessId] = useState(null);
  const [switchingSalonId, setSwitchingSalonId] = useState(null);
  const [creatingSalonForBusinessId, setCreatingSalonForBusinessId] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [resetOpen, setResetOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetForceChange, setResetForceChange] = useState(true);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState("");
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const usersSectionRef = useRef(null);
  const highlightSectionTimeoutRef = useRef(null);
  const deepLinkSectionRef = useRef("");

  const businessIdValue = useMemo(
    () => (selectedBusinessId != null ? Number(selectedBusinessId) : null),
    [selectedBusinessId]
  );
  const businessOptionLabel = (business) =>
    `Business ID ${business?.id} \u2014 ${
      business?.name || "(Unnamed business)"
    }`;
  const salonOptionLabel = (salon) =>
    `Salon ID ${salon?.id} — ${salon?.name || "(Unnamed salon)"}`;
  const userBusinessIdValue = useMemo(
    () =>
      selectedUserBusinessId !== "" && selectedUserBusinessId != null
        ? Number(selectedUserBusinessId)
        : null,
    [selectedUserBusinessId]
  );
  const filteredSalons = useMemo(() => {
    const selectedSalonId =
      selectedSalonFilterId !== "" && selectedSalonFilterId != null
        ? Number(selectedSalonFilterId)
        : null;
    const q = salonSearch.trim().toLowerCase();
    const baseList = (salons || []).filter((salon) => {
      if (!selectedSalonId) return true;
      return Number(salon.id) === selectedSalonId;
    });
    if (!q) return baseList;
    return baseList.filter((salon) => {
      const blob = [
        salon.name,
        salon.city,
        salon.province,
        salon.id,
      ]
        .map((value) => String(value || "").toLowerCase())
        .join(" ");
      return blob.includes(q);
    });
  }, [salons, salonSearch, selectedSalonFilterId]);
  const userSalonOptions = useMemo(() => {
    const normalized = Array.isArray(allSalons) ? allSalons : [];
    if (!userBusinessIdValue) return normalized;
    return normalized.filter(
      (salon) => Number(salon.business_id || 0) === Number(userBusinessIdValue)
    );
  }, [allSalons, userBusinessIdValue]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(""), 3000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(
    () => () => {
      if (highlightSectionTimeoutRef.current) {
        clearTimeout(highlightSectionTimeoutRef.current);
      }
    },
    []
  );

  const scrollToSection = useCallback((id) => {
    const target = typeof document !== "undefined" ? document.getElementById(id) : null;
    if (!target) return false;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    return true;
  }, []);

  const jumpToSectionWithHighlight = useCallback(
    (id) => {
      const jumped = scrollToSection(id);
      if (!jumped) return;
      setHighlightedSectionId(id);
      if (highlightSectionTimeoutRef.current) {
        clearTimeout(highlightSectionTimeoutRef.current);
      }
      highlightSectionTimeoutRef.current = setTimeout(() => {
        setHighlightedSectionId((current) => (current === id ? "" : current));
      }, 1500);
    },
    [scrollToSection]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const requestedSection = String(params.get("section") || "").toLowerCase();
    const requestedHash = String(location.hash || "").toLowerCase();
    const targetSectionId =
      requestedSection === "marketplace" || requestedHash === "#sa-marketplace"
        ? "sa-marketplace"
        : "";

    if (!targetSectionId || deepLinkSectionRef.current === targetSectionId) {
      return;
    }

    const timer = window.setTimeout(() => {
      jumpToSectionWithHighlight(targetSectionId);
      deepLinkSectionRef.current = targetSectionId;
    }, 0);

    return () => window.clearTimeout(timer);
  }, [jumpToSectionWithHighlight, location.hash, location.search]);

  const getSectionClassName = useCallback(
    (id, baseClassName) =>
      `${baseClassName} ${
        highlightedSectionId === id
          ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200"
          : ""
      }`,
    [highlightedSectionId]
  );

  const loadUsers = async ({ q, salonId, businessId, page = 1 } = {}) => {
    setLoadingUsers(true);
    try {
      const data = await fetchAllUsers({
        q: q || undefined,
        salon_id: salonId || undefined,
        business_id: businessId || undefined,
        page,
        page_size: USERS_PAGE_SIZE,
      });
      const items = Array.isArray(data) ? data : data?.items || [];
      setUsers(Array.isArray(items) ? items : []);
      setUsersPage(Number(data?.page || page || 1));
      setUsersTotal(Number(data?.total || items.length || 0));
      setUsersTotalPages(Number(data?.total_pages || 1));
      setUsersLoaded(true);
    } catch (err) {
      console.error("Failed to load users", err);
      setError("Unable to load users.");
      setUsers([]);
      setUsersLoaded(true);
    } finally {
      setLoadingUsers(false);
    }
  };

  const reloadBusinessesAndAllSalons = async () => {
    const [businessList, salonList] = await Promise.all([
      fetchAllBusinesses({ light: true }),
      fetchAllSalons(undefined, { light: true }),
    ]);
    setBusinesses(Array.isArray(businessList) ? businessList : []);
    setAllSalons(Array.isArray(salonList) ? salonList : []);
  };

  const reloadFilteredSalons = async (businessIdOverride = businessIdValue) => {
    const salonList = await fetchAllSalons(businessIdOverride ?? undefined, {
      light: true,
    });
    setSalons(Array.isArray(salonList) ? salonList : []);
  };

  const openDeleteBusinessModal = (business) => {
    const blockersText = formatBlockers(business?.delete_blockers);
    const deleteStatusUnknown = business?.can_delete_empty == null;
    setDeleteModal({
      type: "business",
      id: business?.id,
      blockersText,
      title: "Delete Business",
      message: `This will permanently delete Business ID ${business?.id} (and any empty setup shell).`,
      subMessage: deleteStatusUnknown
        ? "Ellyanna will check whether this business is empty before deleting it. This action cannot be undone if deletion is allowed."
        : "This action cannot be undone.",
    });
  };

  const openDeleteSalonModal = (salon) => {
    const blockersText = formatBlockers(salon?.delete_blockers);
    const deleteStatusUnknown = salon?.can_delete_empty == null;
    setDeleteModal({
      type: "salon",
      id: salon?.id,
      businessId: salon?.business_id,
      blockersText,
      title: "Delete Salon",
      message: `This will permanently delete Salon ID ${salon?.id}.`,
      subMessage: deleteStatusUnknown
        ? "Ellyanna will check whether this salon is empty before deleting it. Only empty salons can be deleted."
        : "Only empty salons can be deleted. This action cannot be undone.",
    });
  };

  const closeDeleteModal = () => {
    if (deleteSubmitting) return;
    setDeleteModal(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal?.id || deleteSubmitting) return;
    setDeleteSubmitting(true);
    setError("");
    setNotice("");
    try {
      if (deleteModal.type === "business") {
        await deleteEmptyBusiness(deleteModal.id);
      } else {
        await deleteEmptySalon(deleteModal.id);
      }

      const deletedBusinessId = deleteModal.type === "business" ? Number(deleteModal.id) : null;
      const deletedSalonId = deleteModal.type === "salon" ? Number(deleteModal.id) : null;

      if (deletedBusinessId && Number(selectedBusinessId) === deletedBusinessId) {
        setSelectedBusinessId(null);
        persistSelectedBusinessId(null);
      }
      if (deletedBusinessId && Number(selectedUserBusinessId || 0) === deletedBusinessId) {
        setSelectedUserBusinessId("");
      }
      if (deletedSalonId && Number(selectedSalonFilterId || 0) === deletedSalonId) {
        setSelectedSalonFilterId("");
      }
      if (deletedSalonId && Number(selectedUserSalonId || 0) === deletedSalonId) {
        setSelectedUserSalonId("");
      }
      if (deletedSalonId && Number(quickSalon?.id || 0) === deletedSalonId) {
        setQuickSalon(null);
      }

      await reloadBusinessesAndAllSalons();
      const nextBusinessFilter =
        deletedBusinessId && Number(selectedBusinessId) === deletedBusinessId
          ? null
          : businessIdValue;
      await reloadFilteredSalons(nextBusinessFilter);
      await loadUsers({
        q: userSearch.trim(),
        salonId:
          deletedSalonId && Number(selectedUserSalonId || 0) === deletedSalonId
            ? undefined
            : selectedUserSalonId || undefined,
        businessId:
          deletedBusinessId && Number(selectedUserBusinessId || 0) === deletedBusinessId
            ? undefined
            : userBusinessIdValue || undefined,
      });

      if (deleteModal.type === "business") {
        setNotice(`Business ID ${deleteModal.id} deleted.`);
      } else {
        setNotice(`Salon ID ${deleteModal.id} deleted.`);
      }
      setDeleteModal(null);
    } catch (err) {
      console.error("Failed to delete empty entity", err);
      const payloadBlockers =
        err?.blockers ||
        (err?.payload && typeof err.payload === "object" ? err.payload.blockers : null);
      const blockerSummary = formatBlockers(payloadBlockers);
      const detail =
        (err?.payload && typeof err.payload?.detail === "string" && err.payload.detail) ||
        err?.message ||
        "Cannot delete.";
      setError(
        blockerSummary
          ? `Cannot delete. ${detail}: ${blockerSummary}`
          : `Cannot delete. ${detail}`
      );
    } finally {
      setDeleteSubmitting(false);
    }
  };

  useEffect(() => {
    if (!is_super_admin) return;
    let mounted = true;
    setLoadingBusinesses(true);
    setError("");
    fetchAllBusinesses({ light: true })
      .then((businessList) => {
        if (!mounted) return;
        setBusinesses(Array.isArray(businessList) ? businessList : []);
      })
      .catch((err) => {
        console.error("Failed to load superadmin data", err);
        if (mounted) {
          setError("Unable to load Super Admin console.");
        }
      })
      .finally(() => {
        if (mounted) {
          setLoadingBusinesses(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [is_super_admin]);

  useEffect(() => {
    if (!is_super_admin) return;
    let mounted = true;
    fetchAllSalons(undefined, { light: true })
      .then((salonList) => {
        if (!mounted) return;
        const normalizedSalons = Array.isArray(salonList) ? salonList : [];
        setAllSalons(normalizedSalons);
      })
      .catch((err) => {
        console.error("Failed to load all salons", err);
      });
    return () => {
      mounted = false;
    };
  }, [is_super_admin]);

  useEffect(() => {
    if (!is_super_admin) return;
    if (businessIdValue == null && allSalons.length > 0) {
      setSalons(allSalons);
      setLoadingSalons(false);
      return;
    }
    let mounted = true;
    setLoadingSalons(true);
    fetchAllSalons(businessIdValue, { light: true })
      .then((data) => {
        if (!mounted) return;
        setSalons(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Failed to load salons", err);
        if (mounted) {
          setError("Unable to load salons.");
          setSalons([]);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoadingSalons(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [allSalons, is_super_admin, businessIdValue]);

  useEffect(() => {
    if (!is_super_admin) return;
    if (!usersLoaded && !userSearch.trim() && !selectedUserSalonId) {
      return;
    }
    const q = userSearch.trim();
    const timer = window.setTimeout(() => {
      loadUsers({
        q,
        salonId: selectedUserSalonId,
        businessId: userBusinessIdValue,
        page: 1,
      });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [is_super_admin, userSearch, selectedUserSalonId, userBusinessIdValue, usersLoaded]);

  const requestUsers = (page = 1) => {
    loadUsers({
      q: userSearch.trim(),
      salonId: selectedUserSalonId,
      businessId: userBusinessIdValue,
      page,
    });
  };

  useEffect(() => {
    const nextBusinessId = user?.business_id ?? readStoredSelectedBusinessId() ?? null;
    setSelectedBusinessId(nextBusinessId);
    setSelectedUserBusinessId(nextBusinessId ?? "");
    persistSelectedBusinessId(nextBusinessId);
  }, [user?.business_id]);

  useEffect(() => {
    if (!selectedUserSalonId) return;
    const exists = userSalonOptions.some(
      (salon) => String(salon.id) === String(selectedUserSalonId)
    );
    if (!exists) {
      setSelectedUserSalonId("");
    }
  }, [selectedUserSalonId, userSalonOptions]);

  useEffect(() => {
    if (!selectedSalonFilterId) return;
    const exists = (salons || []).some(
      (salon) => String(salon.id) === String(selectedSalonFilterId)
    );
    if (!exists) {
      setSelectedSalonFilterId("");
    }
  }, [salons, selectedSalonFilterId]);

  useEffect(() => {
    if (!highlightedSalonId) return;
    const element = document.getElementById(`salon-card-${highlightedSalonId}`);
    if (!element) return;
    element.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightedSalonId, filteredSalons]);

  const handleBusinessSwitch = async (businessId) => {
    setError("");
    setNotice("");
    setSwitchingBusinessId(businessId);
    try {
      const data = await switchBusiness(businessId);
      if (data?.access_token) {
        login(data.access_token, {
          ...data,
          superadmin_mode: data.superadmin_mode,
        });
      }
      const nextBusinessId = data?.business_id ?? businessId;
      setSelectedBusinessId(nextBusinessId);
      setSelectedUserBusinessId(nextBusinessId);
      persistSelectedBusinessId(nextBusinessId);
      setSelectedUserSalonId("");
      setNotice(
        `Business context switched to ${
          data?.business_code || `Business ID ${nextBusinessId}`
        }.`
      );
    } catch (err) {
      console.error("Failed to switch business", err);
      setError("Unable to switch business.");
    } finally {
      setSwitchingBusinessId(null);
    }
  };

  const handleSalonSwitch = async (salonId) => {
    setError("");
    setNotice("");
    setSwitchingSalonId(salonId);
    try {
      const data = await switchSalon(salonId);
      if (data?.access_token) {
        login(data.access_token, {
          ...data,
          superadmin_mode: data.superadmin_mode,
        });
      }
      const nextBusinessId = data?.business_id ?? null;
      setSelectedBusinessId(nextBusinessId);
      setSelectedUserBusinessId(nextBusinessId ?? "");
      persistSelectedBusinessId(nextBusinessId);
      setSelectedUserSalonId(String(salonId));
      setNotice(`Now viewing Salon ID ${salonId}.`);
    } catch (err) {
      console.error("Failed to switch salon", err);
      setError("Unable to switch salon.");
    } finally {
      setSwitchingSalonId(null);
    }
  };

  const handleQuickSalonLookup = async () => {
    const parsedSalonId = Number.parseInt(String(quickSalonIdInput || "").trim(), 10);
    if (!Number.isFinite(parsedSalonId) || parsedSalonId <= 0) {
      setQuickSalonError("Enter a valid Salon ID.");
      setQuickSalon(null);
      return;
    }

    setQuickSalonLoading(true);
    setQuickSalonError("");
    setError("");
    try {
      const data = await fetchSalonById(parsedSalonId);
      setQuickSalon(data || null);
      setSelectedBusinessId(data?.business_id ?? null);
      persistSelectedBusinessId(data?.business_id ?? null);
      setSelectedSalonFilterId(String(parsedSalonId));
      setSalonSearch("");
      setSelectedUserBusinessId(data?.business_id ?? "");
      setHighlightedSalonId(parsedSalonId);
      setNotice(`Loaded Salon ID ${parsedSalonId}.`);
    } catch (err) {
      console.error("Failed to fetch salon by id", err);
      setQuickSalonError("Salon not found.");
      setQuickSalon(null);
    } finally {
      setQuickSalonLoading(false);
    }
  };

  const handleCreateSalonAndStartSetup = async (businessIdOverride) => {
    const fallbackId = businessIdValue;
    const nextBusinessId =
      businessIdOverride != null ? Number(businessIdOverride) : Number(fallbackId);
    if (!Number.isFinite(nextBusinessId) || nextBusinessId <= 0) {
      setError("Select a business first.");
      return;
    }

    setCreatingSalonForBusinessId(nextBusinessId);
    setError("");
    setNotice("");
    try {
      const createdSalon = await createSalon({
        business_id: nextBusinessId,
        name: "New Salon",
        timezone: resolveBrowserTimezone(),
      });
      const newSalonId = Number(createdSalon?.id);
      if (!Number.isFinite(newSalonId) || newSalonId <= 0) {
        throw new Error("Failed to create salon.");
      }

      const switched = await switchSalon(newSalonId);
      if (switched?.access_token) {
        login(switched.access_token, {
          ...switched,
          superadmin_mode: switched.superadmin_mode,
        });
      }

      const switchedBusinessId = switched?.business_id ?? nextBusinessId;
      setSelectedBusinessId(switchedBusinessId);
      setSelectedUserBusinessId(switchedBusinessId ?? "");
      persistSelectedBusinessId(switchedBusinessId);
      setSelectedUserSalonId(String(newSalonId));
      setNotice(`Created Salon ID ${newSalonId}. Opening setup...`);
      navigate("/setup?step=1", { replace: true });
    } catch (err) {
      console.error("Failed to create salon and start setup", err);
      setError("Unable to create salon and start setup.");
    } finally {
      setCreatingSalonForBusinessId(null);
    }
  };

  const handleViewQuickSalonUsers = async () => {
    if (!quickSalon?.id) return;
    setError("");
    setSelectedUserBusinessId(quickSalon.business_id ?? "");
    setSelectedUserSalonId(String(quickSalon.id));
    setUserSearch("");
    setLoadingUsers(true);
    try {
      const response = await fetchSalonUsers(quickSalon.id);
      const nextUsers = Array.isArray(response?.users) ? response.users : [];
      setUsers(nextUsers);
      setNotice(`Showing users linked to Salon ID ${quickSalon.id}.`);
    } catch (err) {
      console.error("Failed to fetch salon users", err);
      setError("Unable to load salon users.");
    } finally {
      setLoadingUsers(false);
      if (usersSectionRef.current) {
        usersSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  const handleSharedBusinessFilterChange = useCallback((businessId) => {
    setSelectedBusinessId(businessId);
    persistSelectedBusinessId(businessId);
    setSelectedSalonFilterId("");
  }, []);

  const handleMarketplaceInspectSalon = useCallback(
    ({ businessId, salonId }) => {
      const nextBusinessId =
        businessId != null && businessId !== "" ? Number(businessId) : null;
      handleSharedBusinessFilterChange(nextBusinessId);
      setSelectedSalonFilterId(salonId != null ? String(salonId) : "");
      jumpToSectionWithHighlight("sa-salons");
    },
    [handleSharedBusinessFilterChange, jumpToSectionWithHighlight]
  );

  const openResetModal = (targetUser) => {
    setResetTarget(targetUser);
    setResetPassword("");
    setResetForceChange(true);
    setResetError("");
    setResetOpen(true);
  };

  const closeResetModal = () => {
    if (resetSubmitting) return;
    setResetOpen(false);
    setResetTarget(null);
    setResetPassword("");
    setResetError("");
  };

  const handleResetPassword = async () => {
    if (!resetTarget?.id) return;
    const nextPassword = (resetPassword || "").trim();
    if (nextPassword.length < 8) {
      setResetError("Password must be at least 8 characters.");
      return;
    }
    setResetSubmitting(true);
    setResetError("");
    try {
      await resetUserPassword(resetTarget.id, {
        new_password: nextPassword,
        force_change: resetForceChange,
      });
      setNotice(`Password reset for ${resetTarget.username || resetTarget.email}.`);
      closeResetModal();
      await loadUsers({
        q: userSearch.trim(),
        salonId: selectedUserSalonId,
        businessId: userBusinessIdValue,
      });
    } catch (err) {
      console.error("Failed to reset password", err);
      setResetError("Unable to reset password.");
    } finally {
      setResetSubmitting(false);
    }
  };

  if (!is_super_admin) {
    return (
      <div className="px-4 py-6">
        <div className="mx-auto w-full max-w-[900px] rounded-3xl border border-slate-200 bg-white p-8 shadow-2xl text-center">
          <p className="text-lg font-semibold text-rose-500">
            Super Admin access required.
          </p>
          <p className="text-sm text-slate-600 mt-2">
            Only Super Admin accounts can view this console.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6">
      <div className="mx-auto w-full max-w-[1400px]">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Super Admin Console
            </h1>
          </div>

          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}
            {notice && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {notice}
              </div>
            )}

            <div
              id="sa-overview"
              className={getSectionClassName("sa-overview", "")}
            >
              <ReportHelpCard
                title="Help Guide: Super Admin Console"
                subtitle="Manage business context, salons, and users from one secure console."
                sections={HELP_SECTIONS}
                troubleshooting={TROUBLESHOOTING_ITEMS}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
              <div className="flex flex-wrap gap-2">
                {[
                  ["Overview", "sa-overview"],
                  ["Salons", "sa-salons"],
                  ["Businesses", "sa-businesses"],
                  ["Users", "sa-users"],
                  ["Marketplace", "sa-marketplace"],
                ].map(([label, id]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      if (id === "sa-users" && !usersLoaded && !loadingUsers) {
                        requestUsers(1);
                      }
                      jumpToSectionWithHighlight(id);
                    }}
                    className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <section
              id="sa-salons"
              className={getSectionClassName(
                "sa-salons",
                "rounded-2xl border border-slate-200 bg-white p-4 space-y-4"
              )}
            >
              <div className="space-y-3">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <h2 className="inline-flex items-center gap-2 text-lg font-semibold text-slate-900">
                      Salons
                      <InfoTooltip text={TOOLTIP_TEXT.salons} label="Salons" />
                    </h2>
                    <p className="text-sm text-slate-500">
                      {businessIdValue
                        ? `Filtered by Business ID ${businessIdValue}`
                        : "Showing all salons"}{" "}
                      ({filteredSalons.length})
                    </p>
                  </div>
                  <div className="w-full lg:w-auto">
                    <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                      Quick Salon ID:
                      <InfoTooltip
                        text={TOOLTIP_TEXT.quickAccess}
                        label="Quick Access"
                      />
                    </label>
                    <div className="mt-1 flex w-full items-center gap-2 lg:w-[360px]">
                      <input
                        type="number"
                        min="1"
                        value={quickSalonIdInput}
                        onChange={(event) => setQuickSalonIdInput(event.target.value)}
                        placeholder="Enter Salon ID, e.g., 12"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400"
                      />
                      <button
                        type="button"
                        onClick={handleQuickSalonLookup}
                        disabled={quickSalonLoading}
                        className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                      >
                        {quickSalonLoading ? "Loading..." : "Go"}
                      </button>
                    </div>
                  </div>
                </div>

                {quickSalonError && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {quickSalonError}
                  </div>
                )}

                {quickSalon && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-2">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-sm text-slate-700">
                        Loaded Salon ID {quickSalon.id}: {quickSalon.name}. Highlighted in list.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => handleSalonSwitch(quickSalon.id)}
                          disabled={switchingSalonId === quickSalon.id}
                          className="rounded-full bg-rose-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                        >
                          {switchingSalonId === quickSalon.id
                            ? "Switching..."
                            : "Switch to Salon"}
                        </button>
                        <button
                          type="button"
                          onClick={handleViewQuickSalonUsers}
                          className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                        >
                          View Salon Users
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end text-sm">
                  <div className="flex items-center gap-2">
                    <label className="inline-flex items-center gap-2 text-slate-600">
                      Business:
                      <InfoTooltip
                        text={TOOLTIP_TEXT.businessFilter}
                        label="Business filter"
                      />
                    </label>
                    <select
                      className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm"
                      value={selectedBusinessId ?? ""}
                      onChange={(event) => {
                        const value = event.target.value;
                        const nextBusinessId = value ? Number(value) : null;
                        setSelectedBusinessId(nextBusinessId);
                        persistSelectedBusinessId(nextBusinessId);
                        setSelectedSalonFilterId("");
                      }}
                    >
                      <option value="">All</option>
                      {(businesses || []).map((biz) => (
                        <option key={biz.id} value={biz.id}>
                          {businessOptionLabel(biz)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="inline-flex items-center gap-2 text-slate-600">
                      Salon:
                      <InfoTooltip
                        text={TOOLTIP_TEXT.salonFilter}
                        label="Salon filter"
                      />
                    </label>
                    <select
                      className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm"
                      value={selectedSalonFilterId}
                      onChange={(event) => setSelectedSalonFilterId(event.target.value)}
                    >
                      <option value="">All</option>
                      {(salons || []).map((salon) => (
                        <option key={salon.id} value={salon.id}>
                          {salonOptionLabel(salon)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <input
                    type="text"
                    value={salonSearch}
                    onChange={(event) => setSalonSearch(event.target.value)}
                    placeholder="Search salons (name, city, province, ID)"
                    className="w-full sm:w-[320px] rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400"
                  />
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {filteredSalons.map((salon) => (
                  <div
                    key={salon.id}
                    id={`salon-card-${salon.id}`}
                    className={`rounded-xl border bg-slate-50/60 p-3 space-y-2 ${
                      Number(highlightedSalonId) === Number(salon.id)
                        ? "border-rose-300 ring-2 ring-rose-200"
                        : "border-slate-200"
                    }`}
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{salon.name}</p>
                      <p className="text-xs font-semibold text-rose-600">
                        Salon ID: {salon.id}
                      </p>
                      <p className="text-xs text-slate-500">
                        Business {salon.business_id}
                      </p>
                      <p className="text-xs text-slate-500">
                        {salon.city || "-"}, {salon.province || "-"} |{" "}
                        {salon.is_active ? "Active" : "Inactive"}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        disabled={switchingSalonId === salon.id}
                        onClick={() => handleSalonSwitch(salon.id)}
                        className="rounded-full bg-rose-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                      >
                        {switchingSalonId === salon.id ? "Switching..." : "Switch to Salon"}
                      </button>
                      {salon.can_delete_empty === true ? (
                        <button
                          type="button"
                          onClick={() => openDeleteSalonModal(salon)}
                          className="rounded-full border border-rose-300 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100"
                        >
                          Delete Empty
                        </button>
                      ) : salon.can_delete_empty == null ? (
                        <button
                          type="button"
                          onClick={() => openDeleteSalonModal(salon)}
                          className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                        >
                          Check Delete
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled
                          title={`Blockers: ${formatBlockers(salon.delete_blockers, {
                            nonZeroOnly: false,
                          })}`}
                          className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-400 cursor-not-allowed"
                        >
                          Not Empty
                        </button>
                      )}
                    </div>
                    <InfoTooltip
                      text={TOOLTIP_TEXT.switchSalon}
                      label="Switch to Salon"
                    />
                  </div>
                ))}
                {!loadingSalons && filteredSalons.length === 0 && (
                  businessIdValue && (salons?.length || 0) === 0 ? (
                    <div className="md:col-span-2 xl:col-span-3 rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
                      <h3 className="text-base font-semibold text-slate-900">
                        No salons found for this business
                      </h3>
                      <p className="mt-2 text-sm text-slate-600">
                        Create a salon to begin setup (hours, services, staff, online booking).
                      </p>
                      <button
                        type="button"
                        onClick={() => handleCreateSalonAndStartSetup(businessIdValue)}
                        disabled={creatingSalonForBusinessId === businessIdValue}
                        className="mt-4 rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                      >
                        {creatingSalonForBusinessId === businessIdValue
                          ? "Creating..."
                          : "Create Salon & Start Setup"}
                      </button>
                    </div>
                  ) : businessIdValue ? (
                    <p className="text-sm text-slate-500">
                      No salons match your current filters.
                    </p>
                  ) : (
                    <p className="text-sm text-slate-500">No salons available.</p>
                  )
                )}
                {loadingSalons && (
                  <p className="text-sm text-slate-500">Loading salons...</p>
                )}
              </div>
            </section>

            <section
              id="sa-businesses"
              className={getSectionClassName(
                "sa-businesses",
                "rounded-2xl border border-slate-200 bg-white p-4 space-y-4"
              )}
            >
              <div className="flex items-center justify-between gap-3">
                <h2 className="inline-flex items-center gap-2 text-lg font-semibold text-slate-900">
                  Businesses
                  <InfoTooltip
                    text={TOOLTIP_TEXT.businesses}
                    label="Businesses"
                  />
                </h2>
                <p className="text-sm text-slate-500">
                  {loadingBusinesses
                    ? "Loading..."
                    : `${businesses.length} total`}
                </p>
              </div>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {(businesses || []).map((biz) => (
                  <div
                    key={biz.id}
                    className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2"
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{biz.name}</p>
                      <p className="text-xs text-slate-500">
                        {biz.code} | ID {biz.id}
                      </p>
                    </div>
                    {biz.description && (
                      <p className="text-xs text-slate-600 line-clamp-2">{biz.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        disabled={switchingBusinessId === biz.id}
                        onClick={() => handleBusinessSwitch(biz.id)}
                        className="rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                      >
                        {switchingBusinessId === biz.id ? "Switching..." : "Set Current"}
                      </button>
                      {biz.can_delete_empty === true ? (
                        <button
                          type="button"
                          onClick={() => openDeleteBusinessModal(biz)}
                          className="rounded-full border border-rose-300 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100"
                        >
                          Delete Empty
                        </button>
                      ) : biz.can_delete_empty == null ? (
                        <button
                          type="button"
                          onClick={() => openDeleteBusinessModal(biz)}
                          className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                        >
                          Check Delete
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled
                          title={`Blockers: ${formatBlockers(biz.delete_blockers, {
                            nonZeroOnly: false,
                          })}`}
                          className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-400 cursor-not-allowed"
                        >
                          Not Empty
                        </button>
                      )}
                    </div>
                    <InfoTooltip
                      text={TOOLTIP_TEXT.setCurrentBusiness}
                      label="Set Current"
                    />
                  </div>
                ))}
                {!loadingBusinesses && businesses.length === 0 && (
                  <p className="text-sm text-slate-500">No businesses found.</p>
                )}
              </div>
            </section>

            <section
              id="sa-users"
              ref={usersSectionRef}
              className={getSectionClassName(
                "sa-users",
                "rounded-2xl border border-slate-200 bg-white p-4 space-y-4"
              )}
            >
              <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <h2 className="inline-flex items-center gap-2 text-lg font-semibold text-slate-900">
                    Users
                    <InfoTooltip text={TOOLTIP_TEXT.users} label="Users" />
                  </h2>
                  <p className="text-sm text-slate-500">
                    Username, email, and salon links.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                      Search
                      <InfoTooltip text={TOOLTIP_TEXT.search} label="Search" />
                    </label>
                    <input
                      type="text"
                      value={userSearch}
                      onChange={(event) => setUserSearch(event.target.value)}
                      placeholder="Search username, email, full name"
                      className="w-full sm:w-[280px] rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                      Business
                      <InfoTooltip
                        text={TOOLTIP_TEXT.userBusinessFilter}
                        label="User business filter"
                      />
                    </label>
                    <select
                      className="rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm"
                      value={selectedUserBusinessId}
                      onChange={(event) => {
                        setSelectedUserBusinessId(event.target.value);
                        setSelectedUserSalonId("");
                      }}
                    >
                      <option value="">All businesses</option>
                      {(businesses || []).map((biz) => (
                        <option key={biz.id} value={biz.id}>
                          {businessOptionLabel(biz)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                      Salon
                      <InfoTooltip
                        text={TOOLTIP_TEXT.userSalonFilter}
                        label="User salon filter"
                      />
                    </label>
                    <select
                      className="rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm"
                      value={selectedUserSalonId}
                      onChange={(event) => setSelectedUserSalonId(event.target.value)}
                    >
                      <option value="">All salons</option>
                      {userSalonOptions.map((salon) => (
                        <option key={salon.id} value={salon.id}>
                          {salonOptionLabel(salon)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => requestUsers(1)}
                      disabled={loadingUsers}
                      className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                    >
                      {loadingUsers ? "Loading..." : usersLoaded ? "Refresh" : "Load Users"}
                    </button>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto rounded-2xl border border-slate-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-100/80">
                    <tr className="text-left text-slate-700">
                      <th className="px-3 py-2.5 font-semibold">Username</th>
                      <th className="px-3 py-2.5 font-semibold">Email</th>
                      <th className="px-3 py-2.5 font-semibold">Full Name</th>
                      <th className="px-3 py-2.5 font-semibold">Super Admin</th>
                      <th className="px-3 py-2.5 font-semibold">Active</th>
                      <th className="px-3 py-2.5 font-semibold">Salons</th>
                      <th className="px-3 py-2.5 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingUsers ? (
                      <tr>
                        <td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                          Loading users...
                        </td>
                      </tr>
                    ) : !usersLoaded ? (
                      <tr>
                        <td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                          Users are loaded when you open this section, search, or click Load Users.
                        </td>
                      </tr>
                    ) : users.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                          No users found.
                        </td>
                      </tr>
                    ) : (
                      users.map((row) => (
                        <tr key={row.id} className="border-t border-slate-200">
                          <td className="px-3 py-2.5 text-slate-800">
                            {row.username || "--"}
                          </td>
                          <td className="px-3 py-2.5 text-slate-700">{row.email || "--"}</td>
                          <td className="px-3 py-2.5 text-slate-700">
                            {row.full_name || "--"}
                          </td>
                          <td className="px-3 py-2.5">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                row.is_super_admin
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-slate-100 text-slate-600"
                              }`}
                            >
                              {row.is_super_admin ? "Yes" : "No"}
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                row.is_active
                                  ? "bg-sky-100 text-sky-700"
                                  : "bg-slate-100 text-slate-600"
                              }`}
                            >
                              {row.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-slate-700">
                            {Array.isArray(row.salons) && row.salons.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {row.salons.map((salon) => (
                                  <span
                                    key={`${row.id}-${salon.salon_id}`}
                                    className="inline-flex rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-700"
                                  >
                                    {salon.salon_name} ({salon.role})
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-slate-400">--</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <button
                              type="button"
                              onClick={() => openResetModal(row)}
                              className="rounded-full border border-rose-300 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100"
                            >
                              Reset Password
                            </button>
                            <InfoTooltip
                              text={TOOLTIP_TEXT.resetPassword}
                              label="Reset Password"
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {usersLoaded && (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-sm text-slate-600">
                  <span>
                    Showing {users.length} of {usersTotal} users
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => requestUsers(Math.max(1, usersPage - 1))}
                      disabled={loadingUsers || usersPage <= 1}
                      className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span>
                      Page {usersPage} of {usersTotalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => requestUsers(Math.min(usersTotalPages, usersPage + 1))}
                      disabled={loadingUsers || usersPage >= usersTotalPages}
                      className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </section>

            <div
              className={getSectionClassName("sa-marketplace", "")}
            >
              <SuperAdminMarketplaceSection
                businesses={businesses}
                allSalons={allSalons}
                selectedBusinessId={selectedBusinessId}
                onChangeBusinessId={handleSharedBusinessFilterChange}
                onInspectSalon={handleMarketplaceInspectSalon}
              />
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={Boolean(deleteModal)}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title={deleteModal?.title || "Delete"}
        message={deleteModal?.message}
        subMessage={deleteModal?.subMessage}
        confirmLabel="Delete"
        confirmingLabel="Deleting..."
        isSubmitting={deleteSubmitting}
      />

      <SBINModal
        isOpen={resetOpen}
        onClose={closeResetModal}
        title="Reset User Password"
        size="sm"
        fitContent
        closeOnBackdrop
      >
        <div className="space-y-4">
          <div className="text-sm text-slate-700">
            Reset password for{" "}
            <span className="font-semibold">
              {resetTarget?.username || resetTarget?.email || "this user"}
            </span>
            .
          </div>
          {resetError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {resetError}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700">
              New Password
            </label>
            <input
              type="text"
              value={resetPassword}
              onChange={(event) => setResetPassword(event.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400"
              placeholder="Temp12345"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={resetForceChange}
              onChange={(event) => setResetForceChange(event.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-rose-500 focus:ring-rose-300"
            />
            Force change on next login
          </label>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={closeResetModal}
              className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              disabled={resetSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleResetPassword}
              disabled={resetSubmitting}
              className="rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
            >
              {resetSubmitting ? "Resetting..." : "Reset"}
            </button>
          </div>
        </div>
      </SBINModal>
    </div>
  );
}

